import isNaN from 'lodash/isNaN';
import isNil from 'lodash/isNil';

import { NumberParser } from '@internationalized/number';
// docs:  https://react-spectrum.adobe.com/internationalized/number/index.html

const getDecimalParser = function(locale) {
    const finalLocale = locale || window.navigator.language;
    console.log('finalLocale in getDecimalParser is: ', finalLocale);
    return new NumberParser(finalLocale, {style: 'decimal'});
};

const parseDecimal = function(rawValue, locale) {
    // pass in a string or number to parse, returns the parsed number or NaN
    // uses current browser locale to parse, but you can override by passing in a locale string
    const decimalParser = getDecimalParser(locale);
    return decimalParser.parse(rawValue);
};

const parseFixedDecimal = function(rawValue, decimalPlaces) {
    // same as what you were using before March 28 2025, but now using the new NumberParser from Adobe Spectrum @internationalized/number

    const fixedDecimalDigits = decimalPlaces ?? 4;  // maybe set this default somewhere else???  some project-wide settings?

    // you should NOT need this when just DISPLAYING already-entered values -- because this can change the value... by rounding/truncating
    // for displaying, you can/should use something simpler, like just toNumberOrNull(), then use formatter below 
    // IMPORTANT - make sure max decimal digits match your backend, and your formatter below
    // OLD VERSION return toNumberOrNull(params.newValue);  // returns null if value is null or undefined, otherwise a finite number
    // NEW VERSION -- we will truncate to a specific fixed decimal digits -- also uses same code from toNumberOrNull, but keeping here, for easier encapsulation
    // coerce value to a Number if possible, otherwise return null
    let rawNumber;  // undefined for now... but if we have an actual Number, set it here, BEFORE we call .toFixed()
    if (isNil(rawValue) || rawValue === '') {  // checks for null or undefined or empty string '' - why check empty string here?  we need to be able to clear a cell to no value
        return null;
    } else if (Number.isFinite(rawValue)) {  // if its already a number, just use it as is
        rawNumber = rawValue;
        //return rawValue;
    } else {  // if here, its likely a string, so coerce it, check result, and return accordingly
        let numberOrNaN = parseDecimal(rawValue);  // lodash toNumber() coerces things to a finite number, or NaN.  so must check if NaN afterwards
        if (Number.isNaN(numberOrNaN)) {  // is it for sure NaN?  then return null
            return null;
        } else {  // otherwise we have a number here
            rawNumber = numberOrNaN;
        }
        // return Number.isNaN(numberOrNaN) ? null : numberOrNaN;  // return null instead of NaN... or the number
        // previously used lodash toFinite() but that coerces things to zero instead of NaN
    }
    // now we definitely have a number... need to truncate/round to number of digits... 
    // we will call .toFixed() which CONVERTS TO A STRING, then Number.parseFloat(fixedNumber); to go back to a number
    // NOTE - your serializer may want a string anyways (but seems it can take numbers) so you MAY want to represent things here as strings, not numbers (floats)
    //      - ESPECIALLY IF YOU DO ANY MATH HERE!
    const fixedNumber = rawNumber.toFixed(fixedDecimalDigits);
    return Number.parseFloat(fixedNumber);  // back to a Number and return it
};

const parseAndValidateDecimalNumber = (value, decimalPlaces = 4) => {
    // parse a number from a string or Number to a Number with fixed number of decimal places
    // returns { value: Number, isValid: Boolean }
    // pass in decimalPlaces to round/truncate to that many decimal places
    // NOT YET: this is similar to what we used before March 28 2025, but we added a check for locale and converted commas to periods for parsing if user's locale uses that
    // see here for more:  https://github.com/BrightspaceUI/intl/blob/main/lib/number.js
    let isValid = false;
    let parsedVal;
    // for prices/numbers, we will only accept things that don't return NaN in _.toNumber -- null, '' both return 0 from that, which makes sense, but 'some_string' returns NaN
    if (isNaN(parseDecimal(value))) {
        // if new value cannot be coerced to a number, then ignore it - but DO NOT NECESSARILY USE THE RETURN VALUE FROM toNumber - because '' -> 0 -- but you don't want that... you want '' -> null
        isValid = false;
        parsedVal = null;  // doesn't matter actually
    } else {
        parsedVal = parseFixedDecimal(value, decimalPlaces);
        isValid = true;
    }
    // originally I thought we should check oldValue for some things... if old value was valid, but new value is ambiguous, then don't change it... but I don't think that's necessary?
    return { value: parsedVal, isValid };
}


export {
    getDecimalParser,
    parseDecimal,
    parseFixedDecimal,
    parseAndValidateDecimalNumber,
}